import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Radio,
	RadioGroup,
	Snackbar,
	TextField,
	Typography,
} from '@mui/material';
import { Controller, useForm, useWatch } from 'react-hook-form';

import type { IProviderPaymentCalendarGroupList, IProviderPaymentCalendarViewConfigUpdate } from '../../types';
import {
	getAllProviderPaymentCalendarGroup,
	getProviderPaymentCalendarViewConfig,
	updateProviderPaymentCalendarViewConfig,
} from '../../services';
import { defaultAlertState } from '../../../common/constants';
import { extractErrorMessage } from '../../../quotation/helpers';
import { ShowAlertState } from '../../../common/types';

export type ProviderPaymentCalendarViewConfigModalProps = {
	open: boolean;
	providerId: string;
	providerFantasyName: string;
	onClose: () => void;
	onSaved?: () => void;
};

type FormValues = {
	isGrouped: boolean;
	groupId: number | null;
};

const ProviderPaymentCalendarViewConfigModal: React.FC<ProviderPaymentCalendarViewConfigModalProps> = ({
	open,
	providerId,
	providerFantasyName,
	onClose,
	onSaved,
}: ProviderPaymentCalendarViewConfigModalProps) => {
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [alert, setAlert] = useState<ShowAlertState>(defaultAlertState);
	const [groups, setGroups] = useState<IProviderPaymentCalendarGroupList[]>([]);

	const {
		control,
		handleSubmit,
		reset,
		setValue,
		trigger,
		clearErrors,
		formState: { errors },
	} = useForm<FormValues>({
		defaultValues: { isGrouped: true, groupId: null },
		mode: 'all',
	});

	const isGrouped = useWatch({ control, name: 'isGrouped' });
	const groupId = useWatch({ control, name: 'groupId' });

	const groupsById = useMemo(() => {
		const m = new Map<number, IProviderPaymentCalendarGroupList>();
		groups.forEach((g) => m.set(g.id, g));
		return m;
	}, [groups]);

	useEffect(() => {
		if (groupId) {
			clearErrors('groupId');
		}
	}, [groupId, clearErrors]);

	useEffect(() => {
		if (!open || !providerId) return;
		let cancelled = false;
		setLoading(true);

		(async () => {
			try {
				const [groupsResp, configResp] = await Promise.all([
					getAllProviderPaymentCalendarGroup(),
					getProviderPaymentCalendarViewConfig(providerId),
				]);
				if (cancelled) return;

				const list = (groupsResp?.data ?? []).sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
				setGroups(list);

				const incoming = configResp.data;
				reset({
					isGrouped: incoming ? Boolean(incoming.isGrouped) : true,
					groupId: incoming?.groupId ?? (list.length > 0 ? list[0].id : null),
				});

				await trigger();
			} catch {
				if (!cancelled) setGroups([]);
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [open, providerId, reset, trigger]);

	useEffect(() => {
		if (!open) return;
		if (!isGrouped) {
			clearErrors('groupId');
		} else if (groupId) {
			clearErrors('groupId');
		}
	}, [isGrouped, open, clearErrors, groupId]);

	const onSubmit = async (values: FormValues) => {
		setSaving(true);
		try {
			const payload: IProviderPaymentCalendarViewConfigUpdate = {
				providerId,
				isGrouped: values.isGrouped,
				groupId: values.isGrouped ? values.groupId ?? undefined : undefined,
			};

			await updateProviderPaymentCalendarViewConfig(providerId, payload);
			setAlert({ show: true, severity: 'success', message: 'Configuración guardada' });

			setTimeout(() => {
				onSaved?.();
				onClose();
			}, 800);
		} catch (e: any) {
			setAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(e, 'Error al guardar'),
			});
		} finally {
			setSaving(false);
		}
	};

	const handleCloseAlert = () => setAlert(defaultAlertState);

	const disableClose = saving;

	const canSave = !saving && !loading && (!isGrouped || (isGrouped && !!groupId));

	return (
		<>
			<Dialog
				open={open}
				onClose={(_, reason) => {
					if (disableClose) return;
					if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
					onClose();
				}}
				fullWidth
				maxWidth="sm"
			>
				<DialogTitle>Configuración de agrupación — Vista de calendario de pagos ({providerFantasyName})</DialogTitle>

				<Box component="form" onSubmit={handleSubmit(onSubmit)}>
					<DialogContent>
						{loading ? (
							<Box display="flex" justifyContent="center" py={6}>
								<CircularProgress />
							</Box>
						) : (
							<Box display="flex" flexDirection="column" gap={2} mt={1}>
								<Typography variant="body2" color="text.secondary">
									Definí si este proveedor se muestra como una línea individual o agrupado en el calendario.
								</Typography>

								<Controller
									name="isGrouped"
									control={control}
									render={({ field }) => (
										<FormControl>
											<RadioGroup
												row
												value={field.value ? 'grouped' : 'detail'}
												onChange={(e) => {
													const val = e.target.value === 'grouped';
													field.onChange(val);
													if (!val) {
														setValue('groupId', null);
														clearErrors('groupId');
													}
												}}
											>
												<FormControlLabel value="grouped" control={<Radio size="small" />} label="Agrupado" />
												<FormControlLabel value="detail" control={<Radio size="small" />} label="En detalle" />
											</RadioGroup>
										</FormControl>
									)}
								/>

								{isGrouped && (
									<Controller
										name="groupId"
										control={control}
										rules={{ required: 'Grupo es requerido' }}
										render={({ field }) => (
											<FormControl fullWidth size="small" error={!!errors.groupId}>
												<Autocomplete
													options={groups}
													getOptionLabel={(o) => o?.name ?? ''}
													isOptionEqualToValue={(a, b) => a?.id === b?.id}
													value={field.value ? groupsById.get(field.value) || null : null}
													onChange={(_, v) => {
														field.onChange(v?.id ?? null);
														setTimeout(() => trigger('groupId'), 0);
													}}
													renderInput={(params) => (
														<TextField
															{...params}
															label="Grupo"
															size="small"
															error={!!errors.groupId}
															onBlur={() => trigger('groupId')}
														/>
													)}
												/>
												{errors.groupId && <FormHelperText>{errors.groupId.message}</FormHelperText>}
											</FormControl>
										)}
									/>
								)}
							</Box>
						)}
					</DialogContent>

					<DialogActions>
						<Button onClick={onClose} disabled={saving} size="small">
							Cancelar
						</Button>
						<Button type="submit" variant="contained" disabled={!canSave} size="small">
							{saving ? 'Guardando...' : 'Guardar'}
						</Button>
					</DialogActions>
				</Box>
			</Dialog>

			<Snackbar open={alert.show} autoHideDuration={2000} onClose={handleCloseAlert}>
				<Alert variant="filled" severity={alert.severity}>
					{alert.message}
				</Alert>
			</Snackbar>
		</>
	);
};

export default ProviderPaymentCalendarViewConfigModal;
